import React from 'react';
import { Helmet } from 'react-helmet';
import CalendarComponent from './CalendarComponent';
import BusinessInfoForm from './BusinessInfoForm';
import LogoBookMe from './LogoBookMe';
import { useLandingSettings } from '../hooks/useLandingSettings';

const BookMeLandingPage = () => {

  const handleWhatsappClick = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'BookMe WhatsApp CTA',
      });
    }
  };
  const { settings: landing } = useLandingSettings();
  const hero = landing.hero || {};
  const branding = landing.branding || {};
  const sections = landing.sections || {};
  const theme = landing.theme || {};
  const colors = {
    primary: theme.primary || '#f97316',
    primaryDark: theme.primaryDark || '#ea580c',
    accent: theme.accent || theme.primary || '#f97316',
    background: theme.background || '#ffffff',
    surface: theme.surface || '#f9fafb',
    surfaceAlt: theme.surfaceAlt || '#f3f4f6',
    text: theme.text || '#111827',
    muted: theme.muted || '#6b7280',
    border: theme.border || '#e5e7eb',
    heroFrom: theme.heroFrom || '#fff7ed',
    heroTo: theme.heroTo || '#ffffff',
    cardBg: theme.cardBg || '#ffffff',
    whatsapp: theme.whatsapp || '#22c55e',
    whatsappHover: theme.whatsappHover || '#16a34a',
  };
  const fontFamily =
    theme.fontFamily || 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';
  const whatsappNumber = landing.whatsapp?.number || '17872369189';
  const whatsappMessage = landing.whatsapp?.message || 'Hola, tengo preguntas sobre el sistema Book Me';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
  const heroBackground = hero.backgroundImage || '';
  const withAlpha = (color, alpha) =>
    /^#([0-9a-f]{6})$/i.test(color) ? `${color}${alpha}` : color;
  const heroBackgroundStyle = heroBackground
    ? {
        backgroundImage: `linear-gradient(135deg, ${withAlpha(colors.heroFrom, 'f0')}, ${withAlpha(colors.heroTo, 'f0')}), url(${heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundImage: `linear-gradient(135deg, ${colors.heroFrom}, ${colors.heroTo})` };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background, color: colors.text, fontFamily }}>
      {theme.fontUrl && (
        <Helmet>
          <link rel="stylesheet" href={theme.fontUrl} />
        </Helmet>
      )}

      <header className="w-full px-6 py-4 flex justify-start items-center shadow-sm" style={{ backgroundColor: colors.background }}>
        <LogoBookMe
          logoUrl={branding.logoUrl}
          name={branding.name || 'BookMe'}
          logoAlt={branding.logoAlt}
          accentColor={colors.accent}
          textColor={colors.text}
        />
      </header>

      {/* HERO */}
      <section className="pt-20 pb-16 text-center" style={heroBackgroundStyle}>
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-extrabold mb-4 leading-tight">
            {hero.title}{' '}
            {hero.highlight && <span style={{ color: colors.accent }}>{hero.highlight}</span>}
          </h1>
          <p className="text-lg mb-8" style={{ color: colors.muted }}>
            {hero.description}
          </p>

          {/* CTA */}
          <div className="flex justify-center mt-6">
            <a
              href={hero.ctaLink || '#lead-form'}
              className="inline-block text-white font-bold py-3 px-6 rounded-lg transition hover:brightness-95"
              style={{ backgroundColor: colors.primary }}
            >
              {hero.ctaText || '¿Qué necesitas para tu negocio?'}
            </a>
          </div>

          <div className="mt-12">
            <CalendarComponent />
          </div>
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section className="py-20" style={{ backgroundColor: colors.surface }}>
        <div className="max-w-screen-xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-center mb-2">
            {sections.featuresTitle || 'Funcionalidades clave'}
          </h2>
          {sections.featuresSubtitle && (
            <p className="text-center mb-10" style={{ color: colors.muted }}>{sections.featuresSubtitle}</p>
          )}
          <div className="grid md:grid-cols-3 gap-8">
            {landing.features.map((item) => (
              <div
                key={item.id || item.title}
                className="rounded-lg overflow-hidden shadow-md border"
                style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
              >
                {item.mediaType === 'video' ? (
                  <video
                    src={item.media}
                    className="h-48 w-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img
                  src={item.media}
                  alt={item.title}
                  className="h-64 w-full object-cover"
                  loading="lazy"
                />
                
                )}
                <div className="p-5">
                  <h3 className="text-lg font-bold" style={{ color: colors.accent }}>{item.title}</h3>
                  <p style={{ color: colors.muted }}>{item.desc}</p>
                </div>
              </div>
            ))}
            
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="py-16" style={{ backgroundColor: colors.background }}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-10">
            {sections.testimonialsTitle || 'Lo que dicen nuestros clientes'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {landing.testimonials.map((t, i) => (
              <div
                key={`${t.name}-${i}`}
                className="p-6 rounded-lg shadow-md border"
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}
              >
                <p className="italic mb-4" style={{ color: colors.muted }}>"{t.text}"</p>
                <p className="font-semibold" style={{ color: colors.accent }}>{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

     {/* VISTA DEL SISTEMA */}
<section className="py-20" style={{ backgroundColor: colors.background }}>
  <div className="max-w-6xl mx-auto px-4 text-center">
    <h2 className="text-3xl font-bold mb-4">{sections.galleryTitle || 'Tu sistema se ve así'}</h2>
    <p className="text-lg mb-12" style={{ color: colors.muted }}>
      {sections.gallerySubtitle || 'Panel moderno, adaptable a cualquier dispositivo.'}
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {landing.gallery.map((image, idx) => (
        <img
          key={`${image}-${idx}`}
          src={image}
          alt={`Vista ${idx + 1}`}
          className="rounded-xl shadow-lg border w-full object-cover h-64"
          style={{ borderColor: colors.border }}
          loading="lazy"
        />
      ))}
    </div>
  </div>
</section>

      {/* FORMULARIO INFO DE NEGOCIO */}
      <section id="lead-form" className="py-20 px-4" style={{ backgroundColor: colors.surfaceAlt }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6">
            {sections.formTitle || 'Personaliza tu sistema ideal'}
          </h2>
          <p className="text-lg text-center mb-10" style={{ color: colors.muted }}>
            {sections.formSubtitle || 'Cuéntanos sobre tu negocio y agenda una llamada gratuita. Sin compromiso, sin tarjeta.'}
          </p>
          <BusinessInfoForm />
          <p className="text-sm text-center mt-6" style={{ color: colors.muted }}>
            {sections.formFootnote || 'Más de 120 negocios ya usan Book Me.'}
          </p>
        </div>
      </section>
     {/* Botón flotante: WhatsApp con texto e ícono */}
     <a
  href={whatsappLink}
  target="_blank"
  rel="noopener noreferrer"
  onClick={handleWhatsappClick}
  className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 text-white rounded-full shadow-lg transition-all duration-300 ease-in-out max-w-xs w-full sm:w-auto hover:brightness-95"
  style={{ backgroundColor: colors.whatsapp }}
  aria-label="Habla con un representante por WhatsApp"
>
  {/* Ícono: Representante con headset */}
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6 flex-shrink-0"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12v1a3 3 0 003 3h1v-5H5a5 5 0 0110 0h2a7 7 0 00-7-7zm6 8h-2v5a2 2 0 01-2 2h-1v3h4a3 3 0 003-3v-5a3 3 0 00-2-2z" />
  </svg>

  {/* Texto corto en móvil, largo en sm+ */}
  <span className="font-semibold text-sm">
    <span className="sm:hidden">{landing.whatsapp?.labelMobile || 'Hablar con representante'}</span>
    <span className="hidden sm:inline">{landing.whatsapp?.labelDesktop || '¿Tienes preguntas? Habla con un representante ahora'}</span>
  </span>
</a>


      {/* FOOTER */}
      <footer className="py-6 text-center border-t" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
        <p className="text-sm" style={{ color: colors.muted }}>
          {sections.footerText || '© 2025 Book Me. Todos los derechos reservados.'}{' '}
          <a
            href={sections.footerLinkUrl || 'https://puertoricowindows.com'}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:opacity-80"
            style={{ color: colors.accent }}
          >
            {sections.footerLinkLabel || 'Powered by puertoricowindows.com'}
          </a>
        </p>
      </footer>

    </div>
  );
};

export default BookMeLandingPage;
